import { FC, memo, useCallback, useEffect, useState } from "react";
import styles from "./footer-3.module.css";
import { useFooterLogic } from "../../footer.hooks";
import {
  AppLink,
  categoryNormalize,
  footerUrls,
  makeUrl,
  useAppSearchParams,
  HEADER_CATEGORIES_SHOW_PARAM } from
"@commons-monorepo/index";
import { FOOTER_EVENTS as EVENTS } from "../../events";
import StoreVerificationBadge from "../../../store-verification-badge";
import { TFunction } from "i18next";
import PaymentsMethods from "../../components/payment-methods/payment-methods";

const Footer3 = ({
  storeVerifiedFrom,
  t



}: {storeVerifiedFrom?: string;t: TFunction;}) => {
  const { terms, dataTreatment, landingGrowth } = footerUrls;
  const [searchParams, setSearchParams] = useAppSearchParams();
  const {
    termsConditionsUrl,
    termsConditionsExternalUrl,
    storeMetadata,
    categories,
    partnerSocials,
    footerConfig,
    pathname,
    showSic,
    handleSocialButton,
    handleGoLanding,
    contactToShop,
    logEvent,
    hasOffers,
    hasWholesale,
    hasServices,
    addressActive,
    scheduleResume,
    imageSicWhite,
    imageSicBlack,
    paymentMethods,
    lightMode
  } = useFooterLogic();

  const dataAboutUs = storeMetadata.catalogueAboutUs;
  const showSumerBrand = storeMetadata?.prime?.membershipId == null;

  const handleClickLink = (source: string, option: string) => {
    logEvent(EVENTS.FOOTER_SELECT_LINK, {
      event_subtype: "footer_option",
      user_type: "buyer",
      platform: "web",
      source,
      option
    });
  };

  const showMoreCategories = useCallback(() => {
    searchParams.set(HEADER_CATEGORIES_SHOW_PARAM, "");
    setSearchParams(searchParams);
  }, [searchParams]);

  const StoreData = memo(() =>
  <div className={styles.storeData}>
      <p>{t("footer.contact")}</p>
      <p>
        {storeMetadata.prefixPhone} {storeMetadata.phone}
      </p>
      {scheduleResume &&
    <p className={styles.storeDataSchedule}>{scheduleResume}</p>}

      <p>{addressActive?.address}</p>
    </div>
  );

  const Sic = memo(() => {
    return (
      <div className={styles.sic}>
        <a
          href='https://sicfacilita.sic.gov.co/SICFacilita/index.xhtml'
          target='_blank'
          rel='noreferrer'>

          {footerConfig?.darkMode ?
          <img src={imageSicWhite} alt='SIC' loading='lazy' /> :

          <img src={imageSicBlack} alt='SIC' loading='lazy' />}

        </a>
      </div>);

  });

  const StoreMenu: FC = memo(() =>
  <ul className={styles.menu}>
      {dataAboutUs &&
    <li className={styles.item}>
          <AppLink
        to={`${pathname}/${makeUrl(pathname, `sobre-nosotros`)}`}
        className={styles.link}
        onClick={() => handleClickLink("store", t("footer.aboutUs"))}>

            {t("footer.aboutUs")}
          </AppLink>
        </li>}

      {hasWholesale &&
    <li className={styles.item}>
          <AppLink
        to={`${pathname}/${makeUrl(pathname, `ventas-mayoreo`)}`}
        className={styles.link}
        onClick={() => handleClickLink("store", t("footer.wholesale"))}>

            {t("footer.wholesale")}
          </AppLink>
        </li>}

      {hasOffers &&
    <li className={styles.item}>
          <AppLink
        to={`${pathname}/${makeUrl(pathname, `ofertas`)}`}
        className={styles.link}
        onClick={() => handleClickLink("store", t("footer.seller"))}>

            {t("footer.seller")}
          </AppLink>
        </li>}

      {hasServices &&
    <li className={styles.item}>
          <AppLink
        to={`${pathname}/${makeUrl(pathname, `servicios`)}`}
        className={styles.link}
        onClick={() => handleClickLink("store", t("footer.services"))}>

            {t("footer.services")}
          </AppLink>
        </li>}

    </ul>
  );

  const CategoriesMenu: FC = memo(() =>
  <ul className={styles.menu}>
      <>
        {categories.slice(0, 10).map((category, index) =>
      <li className={styles.item} key={`category-item-${index}`}>
            <AppLink
          to={`${pathname}/${makeUrl(
            pathname,
            `categoria/${categoryNormalize(category.name)}`
          )}`}
          className={styles.link}
          onClick={() => handleClickLink("categories", "custom_category")}>

              {category.name}
            </AppLink>
          </li>
      )}
        {categories.length > 10 &&
      <li className={styles.item}>
            <span
          className={styles.link}
          onClick={() => {
            showMoreCategories();
            handleClickLink("categories", "more_categories");
          }}>

              {t("readMore.see_more")}
            </span>
          </li>}

      </>
    </ul>
  );

  const SocialsMenu: FC = memo(() => {
    return (
      <ul className={styles.menu}>
        {partnerSocials.map((social) =>
        <li
          className={styles.item}
          key={`footer-social-menu-${social.iconName}`}>

            <a
            href={social.url}
            target='_blank'
            aria-label={social.source}
            className={`${styles.link}`}
            onClick={() => {
              handleSocialButton(social.source);
              handleClickLink("social_media", social.source);
            }}
            rel='noreferrer'>

              <span className={`${social.iconName} ${styles.icon}`} />
              <span className={`${styles.label}`}>{social.source}</span>
            </a>
          </li>
        )}
      </ul>);

  });

  const Menu: FC = () =>
  <div className={styles.footerMenu}>
      <nav className={`${styles.section} ${styles.sectionStore}`}>
        <h4 className={styles.title}>{storeMetadata.enterpriseName}</h4>
        <StoreMenu />
      </nav>
      <nav className={`${styles.section} ${styles.sectionHelp}`}>
        <StoreVerificationBadge
        storeVerifiedFrom={storeVerifiedFrom}
        storeMetadata={storeMetadata}
        lightMode={lightMode} />

        <h4 className={styles.title}>{t("footer.sections.help")}</h4>
        <ul className={styles.menu}>
          <li className={`${styles.item} ${styles.sectionStoreData}`}>
            <StoreData />
          </li>
          <li className={styles.item}>
            <span
            className={styles.link}
            onClick={() => {
              contactToShop();
              handleClickLink("help", t("footer.text_button"));
            }}>

              {t("footer.text_button")}
            </span>
          </li>
          <li className={`${styles.item} ${styles.sectionStoreData}`}>
            {termsConditionsExternalUrl ?
          <a
            href={termsConditionsExternalUrl}
            className={styles.link}
            target='_blank'
            rel='noreferrer'>

                {t("footer.terms_button")}
              </a> :

          <AppLink to={termsConditionsUrl} className={styles.link}>
                {t("footer.terms_button")}
              </AppLink>}

          </li>
        </ul>
        {paymentMethods && paymentMethods.length > 0 &&
      <div className={styles.paymentsMethodsDesktop}>
            <PaymentsMethods
          paymentMethods={paymentMethods}
          lightMode={lightMode} />

          </div>}

      </nav>
      {categories.length > 0 &&
    <nav className={`${styles.section} ${styles.sectionCategories}`}>
          <h4 className={styles.title}>{t("footer.sections.categories")}</h4>
          <CategoriesMenu />
        </nav>}

      {partnerSocials.length > 0 &&
    <nav className={`${styles.section} ${styles.sectionSocial}`}>
          <h4 className={styles.title}>{t("footer.sections.follow")}</h4>
          <SocialsMenu />
        </nav>}

      <nav className={`${styles.section} ${styles.sectionTerms}`}>
        <ul className={styles.menu}>
          <li className={`${styles.item}`}>
            <StoreData />
          </li>
          <li className={`${styles.item}`}>
            <div className={styles.termsLink}>
              {t("footer.termsLink__one")}{" "}
              <AppLink to={termsConditionsUrl} className='link'>
                {t("footer.terms_button")}
              </AppLink>{" "}
              {t("footer.termsLink__two")}
            </div>
          </li>
        </ul>
      </nav>
    </div>;


  const Legal: FC = memo(() =>
  <div className={styles.footerLegal}>
      <div className={styles.legalCountry}>
        <strong>{t("footer.sections.country")}:</strong> {storeMetadata.country}
      </div>
      <div className={styles.terms}>
        <p>
          {t("footer.terms__one")}{" "}
          <a href={termsConditionsUrl} className='link' target='_blank' rel='noreferrer'>
            {t("footer.terms_button")}
          </a>{" "}
          <span
          dangerouslySetInnerHTML={{
            __html: t("footer.terms__two", {
              dataTreatmentLink: dataTreatment,
              interpolation: { escapeValue: false }
            })
          }} />

        </p>
        {showSumerBrand && <p>{t("footer.disclaimer")}</p>}
      </div>
    </div>
  );

  const [isFromGoogle, setIsFromGoogle] = useState(false);
  useEffect(() => {
    setIsFromGoogle(document.referrer.includes("google"));
  }, []);

  const LinksDownloads: FC = memo(() => {
    return (
      <div className={styles.footerDownloads}>
        <div className={styles.madeBy}>
          <strong>{t("footer.made_by")}</strong>
          <a
            href={landingGrowth}
            aria-label={t("footer.info")}
            target={"_blank"}
            className={styles.brandLogo}
            onClick={handleGoLanding}
            rel='noreferrer'>

            <span className={`${styles.iconSumerLogo} icon-sumer-logo`} />
          </a>
        </div>
      </div>);

  });

  return (
    <footer className={`${styles.footer} ${lightMode && styles.lightMode}`}>
      <div className={styles.footerContainer}>
        <div className={styles.footerGrid}>
          <div className={styles.footerGridMenu}>
            <Menu />
          </div>
          <div className={styles.footerGridLegal}>
            <Legal />
          </div>
          {paymentMethods && paymentMethods.length > 0 &&
          <div className={styles.paymentsMethodsMobile}>
              <hr className={styles.gridDivisor} />
              <PaymentsMethods
              paymentMethods={paymentMethods}
              lightMode={lightMode} />

            </div>}

          {showSumerBrand &&
          <div className={styles.footerGridLinks}>
              <LinksDownloads />
              {showSic && <Sic />}
            </div>}

        </div>
      </div>
    </footer>);

};

export default Footer3;